import type { MouseEventHandler, ReactNode } from 'react';
import { Button, type ButtonState } from '../../atoms/Button/Button';
import styles from './NotificationButton.module.css';

/**
 * Figma: `Buttons/Notification` (260:7498) — "A button that optionally displays
 * a notification indicator. Use to draw attention to pending items such as
 * unread messages, alerts, or action items."
 *
 * (The set was `Buttons/Notifciation` when this was written; the designer fixed
 * the spelling on 2026-08-13. The node id is unchanged.)
 *
 * The set is a thin wrapper around one `Buttons/Button` instance pinned to
 * `Hierarchy=Tertiary, Icon only=True`, with a small indicator absolutely
 * positioned over its top-right corner. Neither hierarchy nor the icon-only
 * geometry varies, so neither is a prop.
 *
 * **The indicator is a dot, not a count.** Until the 2026-09-02 sync it was a
 * `Counter` instance carrying a number; the designer has replaced it with an
 * 8x8 `icon/error` ellipse (`Ellipse 1`, ABSOLUTE at x=22, y=-2 over the 28px
 * button) and deleted the `component/counter/*` tokens that backed the pill. So
 * there is nothing left to render a number *on*, and the API says presence
 * rather than quantity: `unread` is a boolean, and `count`, `counterType`,
 * `max` and `countLabel` are gone.
 *
 * Its single variant property, `State`, is `Default` / `Active`, and the
 * annotation reads "Active — Button with a visible counter badge". Comparing
 * the two variants confirms it: every binding on the inner button is identical
 * across them — same surface, stroke and icon colour — and the *only* difference
 * is whether the dot is present. So `State` is not interaction state and does
 * not become an enum prop; the dot is driven by `unread`, the data that decides
 * it. Hover and disabled remain the button's own real states.
 */
export type NotificationButtonProps = {
  /**
   * Figma `🔀 Icon leading swap` (instance swap) — a slot, not an enum. The
   * design's default instance is the `inbox` icon.
   */
  icon: ReactNode;
  /**
   * Accessible name for the button. Required: an icon-only control has no text
   * to name it. Not in Figma — Figma has no concept of an accessible name.
   */
  label: string;
  /** Figma `State=Active` — draws the indicator dot. */
  unread?: boolean;
  /**
   * How the dot is announced, appended to `label` → "Inbox, unread
   * notifications". Not in Figma.
   */
  unreadLabel?: string;
  disabled?: boolean;
  href?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  /** Force the button's visual state for documentation and visual regression. */
  state?: ButtonState;
  className?: string;
};

export function NotificationButton({
  icon,
  label,
  unread = false,
  unreadLabel = 'unread notifications',
  disabled = false,
  href,
  onClick,
  state,
  className,
}: NotificationButtonProps) {
  /*
   * Not in Figma. The dot sits outside the button in the design's layer tree and
   * carries no text, so left alone it would be invisible to a screen reader —
   * the one control would announce "Inbox" whether or not anything is pending.
   * Folding it into the button's own accessible name makes one control that
   * announces "Inbox, unread notifications", and the dot is then hidden from
   * assistive tech so it is not described twice.
   */
  const accessibleName = unread ? `${label}, ${unreadLabel}` : label;

  return (
    <span className={className ? `${styles.wrapper} ${className}` : styles.wrapper}>
      <Button
        hierarchy="tertiary"
        iconLeading={icon}
        aria-label={accessibleName}
        disabled={disabled}
        href={href}
        onClick={onClick}
        state={state}
      />
      {unread && <span className={styles.dot} aria-hidden="true" />}
    </span>
  );
}
